/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import {
    GeneratedQAsResult,
    GenerateQnAsJobCreateSwagger
} from 'src/languageStudio/appShell/prebuiltDemos/generateQuestionsDemo/generateQuestionsSwagger';
import {
    QuestionAnsweringInputDocument,
    QuestionAnsweringResult
} from 'src/languageStudio/core/services/questionsAnswering/questionsAnsweringSwagger';

/**
 * Represents a service for questions answering demo actions.
 */
export const getQuestionsAnsweringService = createService(({ context, getEnvironment }) => {
    const { apiUrl: runtimeApiUrl } = getEnvironment({ apiName: 'qnaRuntime' });
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, true)
        .build();

    /**
     * Gets answers for the given question and the source text.
     *
     * @param param0.question Input question to be answered.
     * @param param0.records A list of the source text records to search into.
     * @param param0.language (Optional) the 2 letter ISO 639-1 representation of a language.
     * For example, use "en" for English; "es" for Spanish etc.
     */
    const getAnswers = async ({
        question,
        records,
        language = 'en'
    }: {
        question: string;
        language?: string;
        records: QuestionAnsweringInputDocument[];
    }): Promise<QuestionAnsweringResult> => {
        const postUrl = urlBuilder()
            .baseUrl(runtimeApiUrl)
            .resource(':query-text')
            .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion)
            .build();

        const { data: questionAnsweringResult } = await httpService.post<QuestionAnsweringResult>(
            postUrl,
            { records, question, language },
            apiOptions
        );

        return questionAnsweringResult;
    };

    const generateQnAs = async ({
        id,
        documentType,
        content,
        language = 'en',
        operationHandle
    }: {
        id: string;
        documentType: string;
        content: string;
        language?: string;
        operationHandle: OperationHandler<GenerateQnAsJobCreateSwagger>;
    }) => {
        const { headers } = await httpService.post<GeneratedQAsResult>(
            urlBuilder()
                .baseUrl(runtimeApiUrl)
                .resource('generate-questionanswers')
                .queryParam('api-version', LuisConstants.QuestionGeneration.ApiVersion)
                .build(),
            {
                documents: [{ id, documentType, content, language }],
                parameters: { fetchRawContent: true, maxQACount: LuisConstants.QuestionGeneration.NumberOfGeneratedQAPairsPerPage }
            },
            apiOptions
        );
        const jobUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.LocationHeader];
        await createOperationCall(
            jobUrl,
            apiOptions,
            operationHandle,
            undefined,
            undefined,
            LuisConstants.QnaMaker.LroPollMaxNumberOfTrials
        );
    };

    return { getAnswers, generateQnAs };
});
